import { Alert, Box, Chip, Typography, useTheme } from "@mui/material"
import ContiSpecificButton from "components/ContiSpecificButton"
import { isAte, isMobile, isVDO } from "utils/Global"
import HighlightOffIcon from "@mui/icons-material/HighlightOff"
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import { useTranslation } from "react-i18next"
import { useProduct } from "contexts/ProductContext"
import { MAPPStatus } from "components/MappCodeCard/types"
import { useEffect, useMemo, useState } from "react"
import { useAuthenticityCheckStore } from "api/services/AuthenticityCheck"
import { fonts_Conti, fonts_VDO } from "themes/default/tokens"

const CONTACT_LINKS = {
  en: "https://www.continental-aftermarket.com/en-en/contact-authenticity-check-and-vehicle-use",
  de: "https://www.continental-aftermarket.com/de-de/kontakt-originalitaetspruefung-und-fahrzeugverwendung",
  fr: "https://www.continental-aftermarket.com/fr-fr/contacter-le-controle-doriginalite-et-lutilisation-du-vehicule",
  es: "https://www.continental-aftermarket.com/es-es/contacto-control-de-originalidad-y-uso-del-vehiculo",
  it: "https://www.continental-aftermarket.com/it-it/contatto-controllo-originalita-e-utilizzo-veicolo",
}

const SEVERITY = {
  GREEN: "success",
  YELLOW: "warning",
  RED: "error",
}
const BG_COLOR = {
  GREEN: "#489258",
  YELLOW: "#F68900",
  RED: "#C85F5F",
}

export default function MappResult() {
  const { t, i18n } = useTranslation()
  const { productCode, tempProductCode } = useProduct()
  const theme = useTheme()
  const authenticityCheckStore = useAuthenticityCheckStore()
  const { authenticityCheckList } = authenticityCheckStore
  const [code, setCode] = useState(tempProductCode || productCode)

  useEffect(() => {
    if (sessionStorage.getItem("productCode")) {
      setCode(sessionStorage.getItem("productCode"))
    }
  }, [])

  const contactLink = useMemo(
    () =>
      CONTACT_LINKS[i18n.language as keyof typeof CONTACT_LINKS] ||
      CONTACT_LINKS.en,
    [i18n.language]
  )

  const authenticityResult = useMemo(() => {
    return authenticityCheckList
  }, [authenticityCheckList])

  if (!authenticityResult) return null

  return (
    authenticityResult && (
      <Box sx={{ width: "100%" }}>
        <Alert
          iconMapping={{
            success: (
              <CheckCircleOutlineOutlinedIcon
                sx={{ width: "24px", height: "24px" }}
              />
            ),
            warning: (
              <ErrorOutlineIcon sx={{ width: "24px", height: "24px" }} />
            ),
            error: <HighlightOffIcon sx={{ width: "24px", height: "24px" }} />,
          }}
          severity={SEVERITY[authenticityResult.codeFlag]}
          variant="filled"
          sx={{
            backgroundColor: BG_COLOR[authenticityResult.codeFlag],
            color: theme.tokens.color.conti_typo,
            marginBottom: "16px",
            borderRadius: "8px",
          }}
        >
          {t(`mappCodeCard.productText_${authenticityResult.codeFlag}`)}
        </Alert>

        <Box sx={{ margin: "20px 0px" }}>
          {authenticityResult.codeFlag !== MAPPStatus.GREEN && (
            <Typography sx={{ fontSize: "18px", marginBottom: "20px" }}>
              {authenticityResult?.responseText}
            </Typography>
          )}
          <Box sx={{ width: "max-content" }}>
            <ContiSpecificButton
              path={contactLink}
              text={t("buttonTextReport")}
              hidden={authenticityResult.codeFlag === MAPPStatus.GREEN}
              isForwardIcon={true}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "16px",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: fonts_Conti.fontWeight_heavy,
                lineHeight: "33.3px",
                marginBottom: "4px",
              }}
            >
              {t("mappCodeCard.mappCodeTitle")}
            </Typography>

            <Box>
              <Chip
                sx={{
                  height: "auto",
                  width: "100%",
                  justifyContent: "flex-start",
                  "& .MuiChip-label": {
                    display: "block",
                    whiteSpace: "normal",
                    width: isMobile ? "100%" : "max-content",
                  },
                }}
                label={
                  <Typography
                    sx={{
                      width: "245px",
                      fontSize: "18px",
                      fontFamily: isAte
                        ? "Etelka Light"
                        : isVDO
                        ? fonts_VDO.fontFamily
                        : "ContinentalStagSans-Light",
                      fontWeight: fonts_Conti.fontWeight,
                      lineHeight: "1.5",
                      letterSpacing: "3px",
                      wordBreak: "break-all",
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    }}
                  >
                    {authenticityResult?.code || code}
                  </Typography>
                }
              />
            </Box>
          </Box>
          {authenticityResult.codeFlag === MAPPStatus.RED && (
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: fonts_Conti.fontWeight_heavy,
                  lineHeight: "33.3px",
                  marginBottom: "16px",
                }}
              >
                {t("mappCodeCard.questionText")}
              </Typography>
              <Typography
                sx={{
                  fontSize: fonts_Conti.fontSize_C_H,
                }}
              >
                {t("contactText")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    )
  )
}
